<!-- 合同详情组件 -->
<template>
  <ContentWrap>
    <el-collapse v-model="activeNames">
      <el-collapse-item name="contractInfo">
        <template #title>
          <span class="text-base font-bold">基本信息</span>
        </template>
        <el-descriptions :column="4">
          <el-descriptions-item label="合同编号">{{ contract.no }}</el-descriptions-item>
          <!-- 2026-09-21 按原型补充 -->
          <el-descriptions-item label="订单编号">{{
            contract.orderNo || '-'
          }}</el-descriptions-item>
          <el-descriptions-item label="合同名称">{{ contract.name }}</el-descriptions-item>
          <el-descriptions-item label="客户名称">{{ contract.customerName }}</el-descriptions-item>
          <el-descriptions-item label="商机名称">{{ contract.businessName }}</el-descriptions-item>
          <el-descriptions-item label="合同类型">
            <dict-tag
              v-if="contract.contractType"
              :type="DICT_TYPE.CRM_CONTRACT_TYPE"
              :value="contract.contractType"
            />
            <span v-else>-</span>
          </el-descriptions-item>
          <el-descriptions-item label="项目名称">{{
            contract.projectName || '-'
          }}</el-descriptions-item>
          <el-descriptions-item label="项目类型">{{
            contract.projectType || '-'
          }}</el-descriptions-item>
          <!-- ★ 签约日期是独立字段，不要与「下单时间」/「合同开始时间」混看 -->
          <el-descriptions-item label="签约日期">
            {{ formatDate(contract.signDate) || '-' }}
          </el-descriptions-item>
          <!-- 字段仍是 end_time，label 按原型改成「到期日期」 -->
          <el-descriptions-item label="到期日期">
            {{ formatDate(contract.endTime) }}
          </el-descriptions-item>
          <el-descriptions-item label="下单时间">
            {{ formatDate(contract.orderDate) }}
          </el-descriptions-item>
          <el-descriptions-item label="合同开始时间">
            {{ formatDate(contract.startTime) }}
          </el-descriptions-item>
          <el-descriptions-item label="合同金额（元）">
            {{ erpPriceInputFormatter(contract.totalPrice) }}
          </el-descriptions-item>
          <el-descriptions-item label="收/付款">
            <dict-tag
              v-if="contract.paymentDirection != null"
              :type="DICT_TYPE.CRM_CONTRACT_PAYMENT_DIRECTION"
              :value="contract.paymentDirection"
            />
            <span v-else>-</span>
          </el-descriptions-item>
          <el-descriptions-item label="付款方式">
            <dict-tag
              v-if="contract.paymentMethod"
              :type="DICT_TYPE.CRM_CONTRACT_PAYMENT_METHOD"
              :value="contract.paymentMethod"
            />
            <span v-else>-</span>
          </el-descriptions-item>
          <el-descriptions-item label="付款分期">
            <dict-tag
              v-if="contract.installment"
              :type="DICT_TYPE.CRM_CONTRACT_INSTALLMENT"
              :value="contract.installment"
            />
            <span v-else>-</span>
          </el-descriptions-item>
          <el-descriptions-item label="续费说明">{{
            contract.renewDesc || '-'
          }}</el-descriptions-item>
          <el-descriptions-item label="续费费用">
            {{ contract.renewFee != null ? erpPriceInputFormatter(contract.renewFee) : '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="客户签约人">
            {{ contract.signContactName }}
          </el-descriptions-item>
          <el-descriptions-item label="公司签约人">
            {{ contract.signUserName }}
          </el-descriptions-item>
          <el-descriptions-item label="备注">
            {{ contract.remark }}
          </el-descriptions-item>
          <!-- 2026-09-21 按原型补充：说明（与「备注」remark 语义不同，两者并存） -->
          <el-descriptions-item label="说明" :span="4">
            {{ contract.description || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="服务说明" :span="4">
            {{ contract.serviceDesc || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="合同内容" :span="4">
            {{ contract.contractContent || '-' }}
          </el-descriptions-item>
          <!-- ★ 「合同状态」（即审批状态 auditStatus）按需求隐藏（2026-09-21）：审批入口已下线，
               该值恒为「未提交」。恢复：取消注释即可。 -->
          <!-- <el-descriptions-item label="合同状态">
            <dict-tag :type="DICT_TYPE.CRM_AUDIT_STATUS" :value="contract.auditStatus" />
          </el-descriptions-item> -->
        </el-descriptions>
      </el-collapse-item>
      <el-collapse-item name="fileInfo">
        <template #title>
          <span class="text-base font-bold">附件与文件</span>
        </template>
        <el-descriptions :column="1">
          <el-descriptions-item label="合同上传">
            <template v-if="splitUrls(contract.contractFileUrls).length">
              <el-link
                v-for="url in splitUrls(contract.contractFileUrls)"
                :key="url"
                :href="url"
                class="mr-10px"
                target="_blank"
                type="primary"
              >
                {{ fileName(url) }}
              </el-link>
            </template>
            <span v-else>-</span>
          </el-descriptions-item>
          <el-descriptions-item label="合同附件上传">
            <template v-if="splitUrls(contract.attachmentUrls).length">
              <el-link
                v-for="url in splitUrls(contract.attachmentUrls)"
                :key="url"
                :href="url"
                class="mr-10px"
                target="_blank"
                type="primary"
              >
                {{ fileName(url) }}
              </el-link>
            </template>
            <span v-else>-</span>
          </el-descriptions-item>
          <el-descriptions-item label="相关文件">
            <template v-if="splitUrls(contract.relatedFiles).length">
              <el-link
                v-for="url in splitUrls(contract.relatedFiles)"
                :key="url"
                :href="url"
                class="mr-10px"
                target="_blank"
                type="primary"
              >
                {{ fileName(url) }}
              </el-link>
            </template>
            <span v-else>-</span>
          </el-descriptions-item>
          <el-descriptions-item label="抄送给">{{ ccUserNames }}</el-descriptions-item>
        </el-descriptions>
      </el-collapse-item>
      <el-collapse-item name="systemInfo">
        <template #title>
          <span class="text-base font-bold">系统信息</span>
        </template>
        <el-descriptions :column="4">
          <el-descriptions-item label="负责人">{{ contract.ownerUserName }}</el-descriptions-item>
          <el-descriptions-item label="最后跟进时间">
            {{ formatDate(contract.contactLastTime) }}
          </el-descriptions-item>
          <el-descriptions-item label="">&nbsp;</el-descriptions-item>
          <el-descriptions-item label="">&nbsp;</el-descriptions-item>
          <el-descriptions-item label="创建人">{{ contract.creatorName }}</el-descriptions-item>
          <!-- ★ 申请人部门 = 申请人（creator）的部门；与「负责人」的部门口径不同 -->
          <el-descriptions-item label="申请人部门">
            {{ contract.creatorDeptName || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="创建时间">
            {{ formatDate(contract.createTime) }}
          </el-descriptions-item>
          <el-descriptions-item label="更新时间">
            {{ formatDate(contract.updateTime) }}
          </el-descriptions-item>
        </el-descriptions>
      </el-collapse-item>
    </el-collapse>
  </ContentWrap>
</template>
<script lang="ts" setup>
import * as ContractApi from '@/api/crm/contract'
import * as UserApi from '@/api/system/user'
import { formatDate } from '@/utils/formatTime'
import { DICT_TYPE } from '@/utils/dict'
import { erpPriceInputFormatter } from '@/utils'

defineOptions({ name: 'ContractDetailsInfo' })
const props = defineProps<{
  contract: ContractApi.ContractVO
}>()

// 展示的折叠面板
const activeNames = ref(['contractInfo', 'fileInfo', 'systemInfo'])

/** 附件字段存的是「多个 url 用逗号分隔」的字符串，这里拆成数组 */
const splitUrls = (value?: string) =>
  (value ?? '')
    .split(',')
    .map((url) => url.trim())
    .filter((url) => url.length > 0)

/** 从 url 里取文件名做展示文案（取不到就退回原始 url） */
const fileName = (url: string) => {
  const name = url.split('/').pop()?.split('?')[0]
  if (!name || name.length === 0) {
    return url
  }
  // ★ 必须 decodeURIComponent：后端 formatFileUrl 用 UriUtils.encodePathSegment 编码路径，
  //   中文文件名在 url 里是 %E4%BA%91...，不解码会把编码串直接显示给用户。
  try {
    return decodeURIComponent(name)
  } catch {
    return name
  }
}

/** 抄送给：`ccUserIds` 只有 id，这里解析成昵称 */
const userOptions = ref<UserApi.UserVO[]>([])
const ccUserNames = computed(() => {
  const ids = props.contract.ccUserIds ?? []
  if (ids.length === 0) {
    return '-'
  }
  return ids
    .map((id) => userOptions.value.find((user) => user.id === id)?.nickname ?? String(id))
    .join('、')
})

onMounted(async () => {
  userOptions.value = await UserApi.getSimpleUserList()
})
</script>
